
import instance from "../auth/axios";
import { requestData } from "./requestUtils";

// Get all units for the authenticated user (optionally filtered by site_id).
/**
 * Fetches Units for the authenticated user, optionally filtered by Site.
 *
 * @param {string|number|null} [siteId=null] - Optional Site identifier.
 * @param {Object} [options] - Request options.
 * @param {AbortSignal} [options.signal] - Signal used to cancel this read request.
 * @returns {Promise<Object|undefined>} API response payload.
 */
const getUserUnits = (siteId = null, { signal } = {}) => {
  const url = siteId ? `/units?site_id=${siteId}` : "/units";
  return requestData(instance.get(url, { signal }), {
    errorMessage: "Error fetching user units:",
    quietCancellation: true,
  });
};

// Get units for a specific site.
/**
 * Fetches detailed Units for one Site.
 *
 * @param {string|number} siteId - Site identifier.
 * @param {Object} [options] - Request options.
 * @param {AbortSignal} [options.signal] - Signal used to cancel this read request.
 * @returns {Promise<Object|undefined>} API response payload.
 */
const getSiteUnits = (siteId, { signal } = {}) => {
  return requestData(instance.get(`/units/site/${siteId}`, { signal }), {
    errorMessage: "Error fetching site units:",
    quietCancellation: true,
  });
};

// Get a specific unit by ID.
/**
 * Fetches one Unit by identifier.
 *
 * @param {string|number} unitId - Unit identifier.
 * @param {Object} [options] - Request options.
 * @param {AbortSignal} [options.signal] - Signal used to cancel this read request.
 * @returns {Promise<Object|undefined>} API response payload.
 */
const getUnitById = (unitId, { signal } = {}) => {
  return requestData(instance.get(`/units/${unitId}`, { signal }), {
    errorMessage: "Error fetching unit:",
    quietCancellation: true,
  });
};

// Create a new unit.
// Mutations are intentionally not cancelled because cancellation cannot prove
// whether the server persisted the mutation.
/**
 * Creates a Unit.
 *
 * Mutation requests are intentionally not cancelled because client cancellation cannot
 * prove that the server did not persist the mutation.
 *
 * @param {Object} unitData - Unit values to create.
 * @returns {Promise<Object|undefined>} API response payload.
 */
const createUnit = unitData => {
  return requestData(instance.post("/units", unitData), { errorMessage: "Error creating unit:" });
};

// Update a unit.
/**
 * Updates a Unit.
 *
 * @param {string|number} unitId - Unit identifier.
 * @param {Object} unitData - Unit fields to update.
 * @returns {Promise<Object|undefined>} API response payload.
 */
const updateUnit = (unitId, unitData) => {
  return requestData(instance.put(`/units/${unitId}`, unitData), { errorMessage: "Error updating unit:" });
};

// Delete a unit.
/**
 * Deletes a Unit.
 *
 * @param {string|number} unitId - Unit identifier.
 * @param {number} expectedVersion - Version captured when the delete confirmation opened.
 * @returns {Promise<Object|undefined>} API response payload.
 */
const deleteUnit = (unitId, expectedVersion) => {
  return requestData(
    instance.delete(`/units/${unitId}`, {
      data: { expected_version: expectedVersion },
    }),
    { errorMessage: "Error deleting unit:" }
  );
};

const getRecentlyDeletedUnits = ({ signal } = {}) =>
  requestData(instance.get("/units/recently-deleted", { signal }));

const restoreUnit = unitId =>
  requestData(instance.post(`/units/${unitId}/restore`));

export {
  getUserUnits,
  getSiteUnits,
  getUnitById,
  createUnit,
  updateUnit,
  deleteUnit,
  getRecentlyDeletedUnits,
  restoreUnit,
};
